/** Entra App Registration roles (must match AssistantApi manifest values). */
export type AssistantRole =
  | 'AssistantSysAdmin'
  | 'AssistantAdmin'
  | 'AssistantDevOps'
  | 'AssistantUser';

/** Highest-first order for display / highestAssistantRole. */
const ASSISTANT_DISPLAY_ORDER: AssistantRole[] = [
  'AssistantSysAdmin',
  'AssistantAdmin',
  'AssistantDevOps',
  'AssistantUser'
];

export const ASSISTANT_ROLES: readonly AssistantRole[] = ASSISTANT_DISPLAY_ORDER;

const ELEVATED_APPS: readonly AssistantRole[] = [
  'AssistantSysAdmin',
  'AssistantAdmin',
  'AssistantDevOps'
];

export function getHighestAssistantRole(roles: string[]): AssistantRole | null {
  for (const role of ASSISTANT_DISPLAY_ORDER) {
    if (roles.includes(role)) return role;
  }
  return null;
}

export function hasAnyAssistantRole(roles: string[]): boolean {
  return ASSISTANT_DISPLAY_ORDER.some((role) => roles.includes(role));
}

/** Elevated My Apps (IT Tools, Cursor Usage, etc.). */
export function canAccessElevatedApps(roles: string[]): boolean {
  return ELEVATED_APPS.some((role) => roles.includes(role));
}

/** Site Administration menu and /admin APIs — SysAdmin only. */
export function canAccessSiteAdmin(roles: string[]): boolean {
  return roles.includes('AssistantSysAdmin');
}

/** Media Library My App — Admin or SysAdmin only (excludes DevOps). */
export function canAccessMediaLibrary(roles: string[]): boolean {
  return roles.includes('AssistantSysAdmin') || roles.includes('AssistantAdmin');
}

export const MEDIA_LIBRARY_APP_ID = 'media-library';

/**
 * Catalog / route gate: User → any Assistant role; Admin/DevOps → elevated triad;
 * SysAdmin → SysAdmin only.
 */
export function hasAssistantRole(roles: string[], required: AssistantRole): boolean {
  if (required === 'AssistantUser') return hasAnyAssistantRole(roles);
  if (required === 'AssistantAdmin' || required === 'AssistantDevOps') {
    return canAccessElevatedApps(roles);
  }
  if (required === 'AssistantSysAdmin') return canAccessSiteAdmin(roles);
  return roles.includes(required);
}

/** @deprecated Prefer canAccessElevatedApps / canAccessSiteAdmin for new gates. */
export function isAssistantAdmin(roles: string[]): boolean {
  return roles.includes('AssistantAdmin');
}

export function isAssistantSysAdmin(roles: string[]): boolean {
  return canAccessSiteAdmin(roles);
}
